/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import Vue from 'vue';
import { debounce } from 'lodash';
import { gsap } from 'gsap';
import 'lazysizes';
import lottie from 'lottie-web';
import scrollama from 'scrollama';
import Rellax from 'rellax';
import zenScroll from 'zenscroll';
import Router from './router';
import 'intersection-observer';

import LogoAnimationLottieBlue from './animation/logo-animation-blue.json';
import LogoAnimationLottieWhite from './animation/logo-animation-white.json';

require('./bootstrap');

require('./components/googlemap-component');
require('./components/header-image-slider-component');
require('./components/home-highlighted-items-component');
require('./components/load-more-component');
require('./components/masonry-grid-component');
require('./components/menu-component');
require('./components/search-component');
require('./components/search-overlay-component');
require('./components/video-component');
require('./components/image-grid-component');
require('./components/aef-live-block');
require('./components/three-columns-block-slider');
require('./components/four-columns-block-slider');
require('./components/projects-filter-component');
require('./components/ticker-component');
require('./components/mobile-projects-slider');
require('./components/faq-component');
require('./components/quote-slider');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            // router: null,
            // isMobile: false,
            // isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            showSearch: false,
            // darkHeader: '1',
            // showFooter: '1',
            scrollController: null,
            logoAnimation: null,
            stagger: false,
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },

        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
    },

    mounted() {
        this.buildLogo();
        this.fadeUpAnimation();

        if (window.location.hash) {
            const el = document.querySelector(window.location.hash);
            zenScroll.to(el, 1000);
        }

        window.addEventListener('scroll', () => {
            if (window.pageYOffset < 100) {
                this.logoAnimation.setDirection(-1);
                this.logoAnimation.play();
            } else {
                this.logoAnimation.setDirection(1);
                this.logoAnimation.play();
            }
        });

        EventBus.$on('navigation-toggle-state', (state) => {
            this.showNavigation = state;
            this.toggleOverflowBody(state);
        });

        EventBus.$on('search-toggle-state', (state) => {
            this.showSearch = state;
            this.toggleOverflowBody(state);
        });
    },

    methods: {
        toggleNavigationMenu() {
            EventBus.$emit('navigation-toggle', this.showNavigation);
        },

        toggleSearch() {
            EventBus.$emit('search-toggle', this.showSearch);
        },

        toggleOverflowBody(state) {
            if (state) {
                window.addEventListener('scroll', this.noScroll);
                return;
            }

            window.removeEventListener('scroll', this.noScroll);
        },

        noScroll() {
            window.scrollTo(0, 0);
        },

        buildLogo() {
            const logoHolder = document.querySelector('.header__logo');
            const isWhite = logoHolder.classList.contains('logo--white');

            if (logoHolder) {
                this.logoAnimation = lottie.loadAnimation({
                    container: logoHolder,
                    renderer: 'svg',
                    loop: false,
                    autoplay: false,
                    animationData: isWhite ? LogoAnimationLottieWhite : LogoAnimationLottieBlue,
                    rendererSettings: {
                        progressiveLoad: false,
                    },
                });
            }
        },

        fadeUpAnimation() {
            const animationElement = [...document.querySelectorAll('.page-fade-up')];
            if (animationElement.length < 1) {
                return;
            }

            // Instantiate the scrollama
            if (this.fadeUpScroll) {
                window.removeEventListener('resize', this.fadeUpScroll.resize);
                this.fadeUpScroll.destroy();
                this.fadeUpScroll = null;
            }

            this.fadeUpScroll = scrollama();

            // setup the instance, pass callback functions
            this.fadeUpScroll
                .setup({
                    step: '.page-fade-up',
                    offset: 0.9,
                    debug: false,
                    once: true,
                })
                .onStepEnter((response) => {
                    const { element, index, direction } = response;
                    if (direction === 'down') {
                        const isDualImage = element.classList.value.search(/dual-image/);

                        if (isDualImage === 0 && this.stagger) {
                            this.staggeredImageAnimation(element);
                            return;
                        }

                        if (isDualImage === 0) {
                            this.stagger = true;
                        }

                        gsap.to(element, 0.8, {
                            alpha: 1,
                            y: 0,
                            ease: 'power3.out',
                        });
                    }
                });

            window.addEventListener('resize', this.fadeUpScroll.resize);
        },

        staggeredImageAnimation(el) {
            setTimeout(() => {
                gsap.to(el, 0.8, {
                    alpha: 1,
                    y: 0,
                    ease: 'power3.out',
                    onComplete: () => {
                        this.stagger = false;
                    },
                });
            }, 300);
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
            console.log(`Is Mobile: ${this.isMobile}`);
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.fadeUpScroll.resize);
        window.removeEventListener('resize', this.fadeUpScrollImages.resize);
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('scroll', this.noScroll);
    },
});
